{#key carouselId}
  <div
    class="carousel-container {fields?.breakpoint} {fields?.breakpointClass} {fields?.hideArrowsMobile ? 'hide-arrows-mobile' : ''}"
    style="
      --swiper-pagination-color: {fields?.paginationColor ?? '#ffffff'};
        --swiper-pagination-inactive-color: {fields?.paginationInactiveColor ?? '#000000'};
        --swiper-pagination-inactive-opacity: {fields?.paginationInactiveOpacity ?? 20}%;
        --swiper-pagination-bullet-width: {fields?.paginationSize ?? 10}px;
        --swiper-pagination-bullet-height: {fields?.paginationSize ?? 10}px;
        aspect-ratio: {fields.width * fields.slidesPerView}/{fields.height};
    "
  >
    {#if carouselId}
      {#if fields?.slides?.length}
        <div id={carouselId} class="carousel">
          {#if fields.showArrows}
            <div bind:this={leftArrow} class="swiper-button-prev swiper-button-prev-{carouselId} {fields.roundedArrows ? 'rounded' : ''}" style={`
              top: ${fields.arrowHeight}%;
              background-color: ${hexToRgb(fields.arrowBackgroundColour, fields.arrowOpacity)};
              fill: ${fields.arrowColour};
            `}>
              <svg xmlns="http://www.w3.org/2000/svg" height="30" viewBox="0 -960 960 960" width="30"><path d="M560-240 320-480l240-240 56 56-184 184 184 184-56 56Z"/></svg>
            </div>
            <div bind:this={rightArrow} class="swiper-button-next swiper-button-next-{carouselId} {fields.roundedArrows ? 'rounded' : ''}" style={`
              top: ${fields.arrowHeight}%;
              background-color: ${hexToRgb(fields.arrowBackgroundColour, fields.arrowOpacity)};
              fill: ${fields.arrowColour};
            `}>
              <svg xmlns="http://www.w3.org/2000/svg" height="30" viewBox="0 -960 960 960" width="30"><path d="M504-480 320-664l56-56 240 240-240 240-56-56 184-184Z"/></svg>
            </div>
          {/if}
          <div class="swiper-wrapper {fields.marquee ? 'linear-animation' : ''}">
            {#each fields.slides as slide}
              {#if slide.type === 'video'}
                {@const videoType = findVideoType(slide.videoUrl)}
                <div class="swiper-slide {fields?.breakpoint} {fields?.breakpointClass}" style="aspect-ratio: {fields.width}/{fields.height}">
                  {#if videoType === 'youtube'}
                    <iframe width="100%" height="100%"
                      src={getVideoSrc(slide.videoUrl)}
                      title="YouTube video player" frameborder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowfullscreen
                    ></iframe>
                  {:else if videoType === 'vimeo'}
                    <iframe width="100%" height="100%"
                      src={getVideoSrc(slide.videoUrl)}
                      title="Vimeo video player" frameborder="0"
                      allow="autoplay; fullscreen"
                      allowfullscreen
                    ></iframe>
                  {:else}
                    <video width="100%" height="auto" autoplay={false} muted={false} controls={true} loop={false}>
                      <source src={slide.videoUrl} type="video/mp4">
                    </video>
                  {/if}
                </div>
              {:else}
                <svelte:element this={slide.url ? 'a' : 'div'} href={slide.url}
                  class="swiper-slide {fields?.breakpoint} {fields?.breakpointClass}" style="aspect-ratio: {fields.width}/{fields.height}"
                >
                  <img optimise="true" loading={isAboveFold ? 'eager' : 'lazy'} src={slide.src} class={fields.imageFit || 'cover'} alt={slide.src ? 'Carousel Image' : ''}/>
                  {#if slide?.showText}
                    <div class="slide-text {slide.textBoxPosition}" style={`
                      border-radius: ${fields?.borderRadius || 0}px;
                      background-color: ${hexToRgb(fields.backgroundColour, fields.opacity)};
                      padding: ${fields?.padding || 0}px;
                      color: ${fields.textColour || '#000000'};
                    `}>
                      {@html slide.text}
                    </div>
                  {/if}
                </svelte:element>
              {/if}
            {/each}
          </div>
          {#if fields.showPagination}
            <div class="swiper-pagination swiper-pagination-{carouselId}"></div>
          {/if}
        </div>
        {#if fields.showThumbnails}
          <div class="thumbnail-container">
            {#each fields.slides as slide, i}
              <div class="thumbnail {fields?.breakpoint} {fields?.breakpointClass}" on:click={() => slideTo(i)} style="aspect-ratio: {fields.width / fields.slidesPerView}/{fields.height}">
                {#if slide.type === 'video'}
                  <img src={slide.videoThumbnail} alt="Video thumbnail"/>
                {:else}
                  <img src={slide.src} alt="Image thumbnail"/>
                {/if}
              </div>
            {/each}
          </div>
        {/if}
      {:else if isLoggedIn}
        <div class="no-slides {fields?.breakpoint} {fields?.breakpointClass}" style="aspect-ratio:{fields.width * fields.slidesPerView}/{fields.height};">
          <h3>Carousel</h3>
          <p>Enter edit mode and select this plugin to begin adding slides</p>
        </div>
      {/if}
    {/if}
  </div>
{/key}

<svelte:window bind:innerWidth={windowWidth} />

<script>
  import { getContext, onMount } from 'svelte'
  import { getVideoSrc, findVideoType } from '../../video/v1.0.0/utils'

  export let fields = {}

  const platform = getContext('platform')

  const isLoggedIn = platform.api.core.isLoggedIn()

  const isAboveFold = platform.api.core.render.isAboveTheFold

  let carouselId

  let windowWidth

  let rightArrow
  let leftArrow

  let swiperInstance = null

  let mounted = false

  let currentSlideIndex = 0

  function slideTo (index) {
    swiperInstance.slideTo(index)
  }

  $: if (fields) {
    init()
  }

  onMount(() => {
    mounted = false
    init()
  })

  function init () {
    if (swiperInstance) {
      swiperInstance.destroy()
      carouselId = ''
    }
    setTimeout(() => {
      carouselId = `carousel-${platform.api.core.uuid()}`
      if (fields?.slides?.length) {
        createSwiper()
      }
    }, 0)
  }

  function hexToRgb (hex, opacity = 100) {
    hex = hex.replace(/^#/, '')

    const bigint = parseInt(hex, 16)
    const r = (bigint >> 16) & 255
    const g = (bigint >> 8) & 255
    const b = bigint & 255

    return `rgba(${r}, ${g}, ${b}, ${opacity / 100})`
  }

  async function createSwiper () {
    const [swiperModule] = await Promise.all([
      import('swiper/js/swiper'),
      import('swiper/css/swiper.css'),
    ])

    const Swiper = swiperModule.default

    swiperInstance = new Swiper(`#${carouselId}`, {
      parallax: false,
      autoplay:
        fields.autoplay || fields.marquee ? {
          delay: fields.marquee ? 1 : parseInt(fields.autoplaySpeed) || 3000,
          disableOnInteraction: false,
        } : false,
      loop: fields.marquee ? true : fields.loop || false,
      direction: 'horizontal',
      effect: fields.effect || 'slide',
      fadeEffect: {
        crossFade: fields.effect === 'fade',
      },
      slidesPerView: fields.marquee ? slidesPerView(fields.slidesPerView) : parseInt(fields.slidesPerView) || 1,
      spaceBetween: parseInt(fields.spaceBetween) || 0,
      speed: parseInt(fields.transitionSpeed) || 1000,
      navigation: {
        nextEl: `.swiper-button-next-${carouselId}`,
        prevEl: `.swiper-button-prev-${carouselId}`,
      },
      pagination: {
        el: `.swiper-pagination-${carouselId}`,
        clickable: true,
      },
    })

    swiperInstance.on('slideChange', function () {
      currentSlideIndex = swiperInstance.activeIndex
    })
    mounted = true
  }

  function slidesPerView (slideQuantity) {
    if (windowWidth < 650) {
      return parseInt(slideQuantity) / 2 || 1
    } else {
      return parseInt(slideQuantity) || 1
    }
  }
</script>

<style lang="scss">.carousel {
  width: 100%;
  max-width: 100vw;
  overflow: hidden;
  position: relative;
}
.carousel :global(h1), .carousel :global(h2), .carousel :global(h3), .carousel :global(h4), .carousel :global(p) {
  margin: 0;
}
.carousel .swiper-wrapper {
  width: 100%;
  height: 100%;
  max-width: 100vw;
}
.carousel .swiper-wrapper .swiper-slide {
  display: block;
  max-width: 100%;
  background-size: cover;
  background-position: center;
}
.carousel .swiper-wrapper .swiper-slide img {
  width: 100%;
  height: 100%;
}
.carousel .swiper-wrapper .swiper-slide img.cover {
  object-fit: cover;
}
.carousel .swiper-wrapper .swiper-slide img.contain {
  object-fit: contain;
}

.thumbnail-container {
  margin-top: 10px;
  display: flex;
  justify-content: center;
  gap: 10px;
}
.thumbnail-container .thumbnail {
  max-width: 200px;
  cursor: pointer;
}
.thumbnail-container .thumbnail img {
  object-fit: cover;
  height: 100%;
  width: 100%;
}

.linear-animation {
  transition-timing-function: linear;
}

:global(.swiper-pagination) :global(.swiper-pagination-bullet) {
  width: var(--swiper-pagination-bullet-width);
  height: var(--swiper-pagination-bullet-height);
  background: var(--swiper-pagination-inactive-color);
  opacity: var(--swiper-pagination-inactive-opacity);
}
:global(.swiper-pagination) :global(.swiper-pagination-bullet-active) {
  background: var(--swiper-pagination-color);
  opacity: 100%;
}
:global(.swiper-button-next::after),
:global(.swiper-button-prev::after) {
  display: none;
}

.swiper-button-prev {
  left: 0px;
}
.swiper-button-prev.rounded {
  left: 15px;
  border-radius: 50%;
}

.swiper-button-next {
  right: 0px;
}
.swiper-button-next.rounded {
  right: 15px;
  border-radius: 50%;
}

.swiper-button-next,
.swiper-button-prev {
  width: 50px;
  height: 50px;
  background-color: rgba(255, 255, 255, 0.197);
  fill: white;
}
.swiper-button-next:hover,
.swiper-button-prev:hover {
  filter: brightness(0.8);
}

.slide-text {
  position: absolute;
  text-align: inherit;
}
.slide-text.top-left {
  top: 0;
  left: 0;
  margin: clamp(35px, 5%, 5%);
}
.slide-text.top-center {
  top: 0;
  left: 50%;
  transform: translate(-50%, 0);
  margin-top: clamp(35px, 5%, 5%);
}
.slide-text.top-right {
  top: 0;
  right: 0;
  margin: clamp(35px, 5%, 5%);
}
.slide-text.middle-left {
  top: 50%;
  left: 0;
  transform: translate(0, -50%);
  margin-left: clamp(35px, 5%, 5%);
}
.slide-text.middle-center {
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
.slide-text.middle-right {
  top: 50%;
  right: 0;
  transform: translate(0, -50%);
  margin-right: clamp(35px, 5%, 5%);
}
.slide-text.bottom-left {
  bottom: 0;
  left: 0;
  margin: clamp(35px, 5%, 5%);
}
.slide-text.bottom-center {
  bottom: 0;
  left: 50%;
  transform: translate(-50%, 0);
  margin-bottom: clamp(35px, 5%, 5%);
}
.slide-text.bottom-right {
  bottom: 0;
  right: 0;
  margin: clamp(35px, 5%, 5%);
}

.no-slides {
  width: 100%;
  outline: dashed 2px #888;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 15px;
  box-sizing: border-box;
  text-emphasis: center;
}

@media screen and (max-width: 768px) {
  .hide-arrows-mobile .swiper-button-next, .hide-arrows-mobile .swiper-button-prev {
    display: none;
  }
}

@media screen and (max-width: 768px) {
  .mobile.square {
    aspect-ratio: 1 !important;
  }
  .mobile.portrait {
    aspect-ratio: 0.75 !important;
  }
  .mobile.landscape {
    aspect-ratio: 1.3333333333 !important;
  }
}

@media screen and (max-width: 1024px) {
  .tablet.square {
    aspect-ratio: 1 !important;
  }
  .tablet.portrait {
    aspect-ratio: 0.75 !important;
  }
  .tablet.landscape {
    aspect-ratio: 1.3333333333 !important;
  }
}</style>
<script context="module">export const svelteCompilerVersion = {"major":4,"minor":2,"patch":18};</script>